import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';

/**
 * Phase D2-UI: Thread drawer — right slide-over showing all messages in a thread.
 * Props:
 *   threadId  — ID of the EmailThread to load
 *   onClose   — callback when the drawer is dismissed
 */
export default function ThreadDrawer({ threadId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!threadId) return;
    api.getThread(threadId).then(setData).catch((e) => toast.push(e.message, { variant: 'error' }));
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reextract(email) {
    setBusy(true);
    try {
      await api.reextractEmail(email._id, { force: true });
      toast.push('Re-extraction triggered');
      // Reload thread data
      const updated = await api.getThread(threadId);
      setData(updated);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  if (!threadId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200,
        }}
      />
      {/* Drawer panel */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 600,
          background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          zIndex: 201, overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border-1)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Email Thread</div>
            {data?.thread && (
              <div style={{ fontSize: 13, color: 'var(--ink-500)', marginTop: 4 }}>
                {data.thread.subjectNormalized || '(no subject)'}
                {' · '}{data.messages?.length || 0} message(s)
              </div>
            )}
            {data?.thread?.linkedEnvelopeId && (
              <div
                className="chip ready chip-sm"
                style={{ marginTop: 8, display: 'inline-block' }}
              >
                Linked to envelope
              </div>
            )}
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close thread">✕</button>
        </div>

        {/* Message list */}
        <div style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!data ? (
            <div className="empty">Loading thread…</div>
          ) : data.messages.length === 0 ? (
            <div className="empty">No messages in this thread.</div>
          ) : (
            data.messages.map((msg) => {
              const isOutbound = msg.direction === 'Outbound';
              return (
                <div
                  key={msg._id}
                  style={{
                    border: '1px solid var(--border-1)',
                    borderRadius: 8,
                    padding: 14,
                    background: isOutbound ? 'var(--surface-1)' : '#fff',
                    borderLeft: `3px solid ${isOutbound ? 'var(--ink-300)' : 'var(--accent)'}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>{isOutbound ? '→' : '←'}</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>
                      {msg.fromName || msg.fromAddress}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--ink-500)', marginLeft: 'auto' }}>
                      {msg.receivedAt ? new Date(msg.receivedAt).toLocaleString() : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                    {msg.subject || '(no subject)'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
                    {(msg.body || '').slice(0, 300)}{(msg.body || '').length > 300 ? '…' : ''}
                  </div>
                  {/* Attachment chips */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                      {msg.attachments.map((att, i) => (
                        <span key={i} className="chip muted chip-sm">
                          📎 {att.filename || att.name || `attachment ${i + 1}`}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Re-extract button for inbound messages */}
                  {!isOutbound && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => reextract(msg)}
                        disabled={busy}
                      >
                        Re-extract from this message
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
